import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IonicModule } from '@ionic/angular';
import { FrontendIconsModule } from '@tablesready/frontend-icons';

@Component({
  selector: 'frontend-size-input',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, FrontendIconsModule],
  templateUrl: './frontend-size-input.component.html',
  styleUrls: ['./frontend-size-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FrontendSizeInputComponent),
      multi: true,
    },
  ],
})
export class FrontendSizeInputComponent implements ControlValueAccessor {
  @Input() min = 1;
  @Input() max = 999;
  @Input() step = 1;
  @Input() defaultValue = 4;

  value = this.defaultValue;
  disabled = false;

  private lastSetValue = this.value;
  private onChange: (value: number) => void = (value: number) => {};
  private onTouched: () => void = () => {};

  writeValue(value: number): void {
    this.value = (value === null || value === undefined) ? this.defaultValue : value;
    this.lastSetValue = this.value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  increment(): void {
    if (this.disabled) {
      return;
    }

    const newValue = Math.min(this.value + this.step, this.max);
    this.updateValue(newValue);
  }

  decrement(): void {
    if (this.disabled) {
      return;
    }

    const newValue = Math.max(this.value - this.step, this.min);
    this.updateValue(newValue);
  }

  onBlur(blurEvent: FocusEvent): void {
    if (!(blurEvent.target as any).value) {
      (blurEvent.target as any).value = this.lastSetValue;
    }
  }

  onInputChange(inputEvent): void {
    const inputValue = parseInt(inputEvent.target.value);

    if (inputValue < this.min) {
      inputEvent.target.value = this.min;
    }

    if (inputValue > this.max) {
      inputEvent.target.value = this.max;
    }

    if (!isNaN(inputValue)) {
      this.updateValue(Math.min(Math.max(inputValue, this.min), this.max));
    }
  }

  private updateValue(newValue: number): void {
    this.value = newValue;
    this.lastSetValue = this.value;
    this.onChange(this.value);
    this.onTouched();
  }
}
